import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  TextInput,
  Textarea,
  Select,
  Button,
  Group,
  Stack,
  Text,
  Badge,
  Avatar,
  Paper,
  ActionIcon,
  FileButton,
  Divider,
  Box,
  TagsInput,
  Tooltip,
  Drawer,
  ScrollArea,
  Image,
  Grid,
  ThemeIcon,
  Card,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { modals } from "@mantine/modals";
import { DatePickerInput } from "@mantine/dates";
import {
  IconTrash,
  IconPaperclip,
  IconDownload,
  IconSend,
  IconEdit,
  IconHistory,
  IconPhoto,
  IconX,
} from "@tabler/icons-react";
import {
  createTask,
  updateTask,
  deleteTask,
  getComments,
  createComment,
  updateComment,
  deleteComment,
  getAttachments,
  uploadAttachment,
  deleteAttachment,
  getTaskHistory,
} from "../../services/workflowService";
import useAuthStore from "../../store/useAuthStore";
import MarkdownEditor from "./MarkdownEditor";
import socketService from "../../services/socketService";
import dayjs from "dayjs";

const WorkflowTaskModal = ({
  opened,
  onClose,
  task,
  columnId,
  columns,
  users,
  onTaskUpdate,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [dueDate, setDueDate] = useState(null);
  const [tags, setTags] = useState([]);
  const [assignedToId, setAssignedToId] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [commentImage, setCommentImage] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentContent, setEditingCommentContent] = useState("");
  const [commentImagePreview, setCommentImagePreview] = useState(null);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [loading, setLoading] = useState(false);
  const [watchers, setWatchers] = useState([]);
  const [localColumnId, setLocalColumnId] = useState(task?.columnId);

  // Auto-save states
  const [saveStatus, setSaveStatus] = useState("saved"); // 'saving', 'saved', 'error'
  const [lastSaved, setLastSaved] = useState(null);

  // Track what user has actually touched/modified
  const [dirtyFields, setDirtyFields] = useState(new Set());

  // Field-level locking
  const [lockedFields, setLockedFields] = useState({}); // { fieldName: userEmail }

  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Preview modal state
  const [previewImage, setPreviewImage] = useState(null);

  const currentUser = useAuthStore((state) => state.user);
  const isAdmin = useAuthStore((state) => state.isAdmin);

  const [activeEditor, setActiveEditor] = useState(null);
  const isEditor = !task || activeEditor === currentUser?.email;
  const canEdit =
    !task ||
    (isEditor &&
      (task.createdById === currentUser?.id ||
        task.assignedToId === currentUser?.id ||
        isAdmin));

  const lastInitializedTaskId = useRef(null);

  const loadComments = useCallback(async () => {
    if (!task?.id) return;
    try {
      const res = await getComments(task.id);
      if (res.success) setComments(res.comments);
    } catch (error) {
      console.error("Error loading comments:", error);
    }
  }, [task?.id]); // Depend only on ID

  const loadAttachments = useCallback(async () => {
    if (!task?.id) return;
    try {
      const res = await getAttachments(task.id);
      if (res.success) setAttachments(res.attachments);
    } catch (error) {
      console.error("Error loading attachments:", error);
    }
  }, [task?.id]); // Depend only on ID

  // 1. Task Initialization & Prop Sync
  useEffect(() => {
    console.log(
      `🔍 WorkflowTaskModal useEffect triggered: task=${task?.id}, isEditor=${isEditor}`,
    );
    if (task) {
      const isDifferentTask = lastInitializedTaskId.current !== task.id;

      // CRITICAL: Viewers must ALWAYS sync from task prop to see other users' changes
      // Editors only sync on initial load to preserve their unsaved edits
      const shouldSync = isDifferentTask || !isEditor;

      if (shouldSync) {
        console.log(
          `🔄 Syncing modal from task prop: isEditor=${isEditor}, isDifferentTask=${isDifferentTask}, taskId=${task.id}`,
        );
        console.log(`   - Old tags: [${tags.join(", ")}]`);
        console.log(`   - New tags: [${(task.tags || []).join(", ")}]`);

        setTitle(task.title || "");
        setDescription(task.description || "");
        setPriority(task.priority || "medium");
        setDueDate(task.dueDate ? new Date(task.dueDate) : null);
        setTags(Array.isArray(task.tags) ? task.tags : []);
        setAssignedToId(task.assignedToId?.toString() || null);
        setIsEditingDescription(false);
        loadComments();
        loadAttachments();
        lastInitializedTaskId.current = task.id;

        // Only clear dirty fields if we're not the editor
        if (!isEditor) {
          setDirtyFields(new Set());
        }
      } else {
        console.log(
          `⏭️ Skipping sync: Editor with unsaved changes (dirtyFields: ${dirtyFields.size})`,
        );
      }

      if (task.columnId && Number(task.columnId) !== Number(localColumnId)) {
        console.log(`🏠 Modal column sync: task.columnId #${task.columnId}`);
        setLocalColumnId(task.columnId);
      }
    } else {
      if (lastInitializedTaskId.current !== null) {
        setTitle("");
        setDescription("");
        setPriority("medium");
        setDueDate(null);
        setTags([]);
        setAssignedToId(null);
        setIsEditingDescription(true);
        setComments([]);
        setAttachments([]);
        setWatchers([]);
        setSaveStatus("saved");
        setLastSaved(null);
        lastInitializedTaskId.current = null;
      }
    }
  }, [
    task,
    isEditor,
    dirtyFields.size,
    loadComments,
    loadAttachments,
    tags,
    localColumnId,
  ]);

  // 2. Socket Sync Logic
  useEffect(() => {
    const taskId = task?.id;
    const userEmail = currentUser?.email;

    if (taskId && userEmail) {
      console.log(`🔌 Initializing socket sync for task #${taskId}`);
      socketService.connect();
      socketService.joinTask(taskId, userEmail);

      const onWatchersUpdate = (updatedWatchers) =>
        setWatchers(updatedWatchers);
      const onCommentUpdate = () => loadComments();
      const onAttachmentUpdate = () => loadAttachments();

      const onFocus = ({ userEmail: focusUser, fieldName }) => {
        if (focusUser === userEmail) return;
        setLockedFields((prev) => ({ ...prev, [fieldName]: focusUser }));
      };

      const onBlur = ({ fieldName }) => {
        setLockedFields((prev) => {
          const newLocks = { ...prev };
          delete newLocks[fieldName];
          return newLocks;
        });
      };

      const onBoardRefresh = (data) => {
        if (data.type === "move" && Number(data.taskId) === Number(taskId)) {
          console.log(
            `🏠 Modal DIRECT move signal: new column #${data.columnId}`,
          );
          setLocalColumnId(data.columnId);
        }
      };

      const onEditorChange = ({ editor }) => {
        console.log(`👤 Editor changed: ${editor}`);
        setActiveEditor(editor);
      };

      const onClearUserLocks = ({ userEmail: clearEmail }) => {
        console.log(
          `🧹 Clearing all locks for disconnected user: ${clearEmail}`,
        );
        setLockedFields((prev) => {
          const newLocks = { ...prev };
          Object.keys(newLocks).forEach((field) => {
            if (newLocks[field] === clearEmail) {
              delete newLocks[field];
            }
          });
          return newLocks;
        });
      };

      socketService.onTaskWatchersUpdate(onWatchersUpdate);
      socketService.onTaskCommentUpdate(onCommentUpdate);
      socketService.onTaskAttachmentUpdate(onAttachmentUpdate);
      socketService.onFieldFocus(onFocus);
      socketService.onFieldBlur(onBlur);
      socketService.onBoardRefresh(onBoardRefresh);
      socketService.socket?.on("task:editor_change", onEditorChange);
      socketService.socket?.on("task:clear_user_locks", onClearUserLocks);

      return () => {
        console.log(`🔌 Cleaning up socket sync for task #${taskId}`);
        socketService.leaveTask(taskId, userEmail);
        socketService.offTaskWatchersUpdate(onWatchersUpdate);
        socketService.offTaskCommentUpdate(onCommentUpdate);
        socketService.offTaskAttachmentUpdate(onAttachmentUpdate);
        socketService.offFieldFocus(onFocus);
        socketService.offFieldBlur(onBlur);
        socketService.offBoardRefresh(onBoardRefresh);
        socketService.socket?.off("task:editor_change", onEditorChange);
        socketService.socket?.off("task:clear_user_locks", onClearUserLocks);
      };
    }
  }, [task?.id, currentUser?.email, loadComments, loadAttachments]); // Corrected dependencies

  // Comment image preview
  useEffect(() => {
    if (commentImage) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCommentImagePreview(reader.result);
      };
      reader.readAsDataURL(commentImage);
    } else {
      setCommentImagePreview(null);
    }
  }, [commentImage]);

  const loadLogs = async () => {
    if (!task) return;
    try {
      setLoadingLogs(true);
      const res = await getTaskHistory(task.id);
      if (res.success) setLogs(res.logs);
    } catch (error) {
      console.error("Error loading logs:", error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleFocus = (fieldName) => {
    if (!task || !currentUser) return;
    socketService.emitFieldFocus(task.id, currentUser.email, fieldName);
  };

  const handleBlur = (fieldName) => {
    if (!task || !currentUser) return;
    socketService.emitFieldBlur(task.id, currentUser.email, fieldName);
  };

  const handleModalClose = () => {
    if (dirtyFields.size > 0 && isEditor) {
      modals.openConfirmModal({
        title: "Unsaved Changes",
        centered: true,
        children: (
          <Text size="sm">
            You have unsaved changes. Do you want to save them before closing,
            or discard them?
          </Text>
        ),
        labels: { confirm: "Save changes", cancel: "Discard changes" },
        confirmProps: { color: "blue" },
        cancelProps: { color: "red", variant: "outline" },
        onCancel: () => {
          setDirtyFields(new Set());
          onClose();
        },
        onConfirm: async () => {
          await handleSave();
        },
      });
    } else {
      onClose();
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const taskData = {
        title,
        description,
        priority,
        dueDate: dueDate ? new Date(dueDate).toISOString() : null,
        tags,
        assignedToId: assignedToId ? parseInt(assignedToId) : null,
        columnId: task ? localColumnId || task.columnId : columnId,
      };

      if (task) {
        await updateTask(task.id, taskData);
        const updatedTask = { ...task, ...taskData };

        notifications.show({
          title: "Success",
          message: "Task updated successfully",
          color: "green",
        });

        // Update parent state immediately (for Editor's UI)
        if (onTaskUpdate) {
          console.log("✅ Calling parent onTaskUpdate to refresh local state");
          onTaskUpdate(true, null, { type: "update", task: updatedTask });
        }

        // Broadcast to other users
        socketService.notifyBoardUpdate({ type: "update", task: updatedTask });
      } else {
        const res = await createTask(taskData);
        if (res.success && onTaskUpdate) {
          notifications.show({
            title: "Success",
            message: "Task created successfully",
            color: "green",
          });
          onTaskUpdate(true, null, { type: "create", task: res.task });
          onClose(); // Close on create!
        }
      }

      setDirtyFields(new Set());
      setSaveStatus("saved");
      setLastSaved(new Date());
      console.log("✅ Task saved successfully, staying in modal.");
    } catch (error) {
      console.error("Error saving task:", error);
      notifications.show({
        title: "Error",
        message: error.response?.data?.message || "Failed to save task",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!task) return;

    modals.openConfirmModal({
      title: (
        <Group gap="xs">
          <IconTrash size={18} color="red" />
          <Text fw={700}>Delete task</Text>
        </Group>
      ),
      centered: true,
      children: (
        <Text size="sm">
          Are you sure you want to delete this task? This action is
          irreversible.
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red", variant: "filled" },
      onConfirm: async () => {
        try {
          await deleteTask(task.id);
          onClose();
        } catch (error) {
          console.error("Error deleting task:", error);
        }
      },
    });
  };

  const handleAddComment = async () => {
    if ((!newComment.trim() && !commentImage) || !task) return;
    try {
      let imageData = null,
        imageName = null,
        imageMimeType = null;
      if (commentImage) {
        const reader = new FileReader();
        imageData = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result.split(",")[1]);
          reader.readAsDataURL(commentImage);
        });
        imageName = commentImage.name;
        imageMimeType = commentImage.type;
      }
      const res = await createComment(
        task.id,
        newComment,
        imageData,
        imageName,
        imageMimeType,
      );
      if (res.success) {
        setComments([...comments, res.comment]);
        setNewComment("");
        setCommentImage(null);
        setCommentImagePreview(null);
      }
    } catch (error) {
      console.error("Error adding comment:", error);
    }
  };

  const handleEditComment = async (commentId) => {
    try {
      const res = await updateComment(commentId, editingCommentContent);
      if (res.success) {
        setComments(
          comments.map((c) => (c.id === commentId ? res.comment : c)),
        );
        setEditingCommentId(null);
      }
    } catch (error) {
      console.error("Error editing comment:", error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    modals.openConfirmModal({
      title: (
        <Group gap="xs">
          <IconTrash size={18} color="red" />
          <Text fw={700}>Delete comment</Text>
        </Group>
      ),
      centered: true,
      children: (
        <Text size="sm">Are you sure you want to delete this comment?</Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red", variant: "filled" },
      onConfirm: async () => {
        try {
          await deleteComment(commentId);
          setComments(comments.filter((c) => c.id !== commentId));
        } catch (error) {
          console.error("Error deleting comment:", error);
        }
      },
    });
  };

  const handleFileUpload = async (file) => {
    if (!file || !task) return;
    handleFocus("attachment");
    try {
      const reader = new FileReader();
      const base64Data = await new Promise((resolve) => {
        reader.onload = (e) => resolve(e.target.result.split(",")[1]);
        reader.readAsDataURL(file);
      });

      const attachmentData = {
        filename: file.name,
        filesize: file.size,
        mimetype: file.type,
        base64Data,
      };

      const res = await uploadAttachment(task.id, attachmentData);
      if (res.success) setAttachments([...attachments, res.attachment]);
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setTimeout(() => handleBlur("attachment"), 1000);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    modals.openConfirmModal({
      title: (
        <Group gap="xs">
          <IconTrash size={18} color="red" />
          <Text fw={700}>Delete attachment</Text>
        </Group>
      ),
      centered: true,
      children: (
        <Text size="sm">Are you sure you want to delete this file?</Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red", variant: "filled" },
      onConfirm: async () => {
        try {
          await deleteAttachment(attachmentId);
          setAttachments(attachments.filter((a) => a.id !== attachmentId));
        } catch (error) {
          console.error("Error deleting attachment:", error);
        }
      },
    });
  };

  const priorityOptions = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Urgent" },
  ];

  const userOptions = users.map((user) => ({
    value: user.id.toString(),
    label: user.email,
  }));

  // Reactive column lookup
  const currentColumn = columns
    ? columns.find((c) => Number(c.id) === Number(localColumnId))
    : null;
  return (
    <>
      <Modal
        opened={opened}
        onClose={handleModalClose}
        title={
          <Box style={{ width: "100%" }}>
            <Group
              justify="space-between"
              align="center"
              style={{ width: "100%" }}
            >
              <Group gap="xs">
                <ThemeIcon size="lg" radius="md" variant="light" color="blue">
                  <IconEdit size={20} />
                </ThemeIcon>
                <Stack gap={0}>
                  <Group gap="xs">
                    <Text fw={700} size="lg">
                      {task ? "Task Details" : "Create New Task"}
                    </Text>
                    {task && !isEditor && (
                      <Badge color="orange" variant="light" size="xs">
                        View Only
                      </Badge>
                    )}
                  </Group>
                  {task && (
                    <Group gap={6}>
                      <Badge
                        variant="dot"
                        color={
                          currentColumn?.color || task.column?.color || "gray"
                        }
                        size="xs"
                      >
                        {currentColumn?.name ||
                          task.column?.name ||
                          "No Column"}
                      </Badge>
                      <Text size="xs" c="dimmed">
                        ID: #{task.id.toString().slice(-4)}
                      </Text>

                      {activeEditor && (
                        <Text size="xs" c={isEditor ? "blue" : "gray"} fw={500}>
                          •{" "}
                          {isEditor
                            ? "You are editing"
                            : `Editor: ${activeEditor.split("@")[0]}`}
                        </Text>
                      )}
                    </Group>
                  )}
                </Stack>
              </Group>

              <Group gap="sm">
                {task && (
                  <Group gap="xs">
                    {saveStatus === "saving" && (
                      <Badge color="gray" variant="dot" size="xs">
                        Saving...
                      </Badge>
                    )}
                    {saveStatus === "saved" && lastSaved && (
                      <Text size="xs" c="dimmed">
                        Saved {dayjs(lastSaved).format("HH:mm:ss")}
                      </Text>
                    )}
                    {saveStatus === "error" && (
                      <Badge color="red" size="xs">
                        Save error!
                      </Badge>
                    )}
                  </Group>
                )}

                {watchers.length > 1 && (
                  <Tooltip
                    label={`Also viewing: ${watchers.filter((e) => e !== currentUser?.email).join(", ")}`}
                  >
                    <Avatar.Group gap="xs">
                      {watchers
                        .filter((e) => e !== currentUser?.email)
                        .slice(0, 3)
                        .map((email) => (
                          <Avatar key={email} size="sm" radius="xl">
                            {email[0].toUpperCase()}
                          </Avatar>
                        ))}
                      {watchers.length > 4 && (
                        <Avatar size="sm" radius="xl">
                          +{watchers.length - 4}
                        </Avatar>
                      )}
                    </Avatar.Group>
                  </Tooltip>
                )}

                {task && (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={() => {
                      setShowLogs(true);
                      loadLogs();
                    }}
                    title="View History"
                  >
                    <IconHistory size={18} />
                  </ActionIcon>
                )}
              </Group>
            </Group>
          </Box>
        }
        classNames={{ content: "workflow-task-dialog", body: "workflow-task-body" }}
        size="85%"
        padding="xl"
        radius="lg"
      >
        <Drawer
          opened={showLogs}
          onClose={() => setShowLogs(false)}
          title="Task History"
          position="right"
          size="md"
        >
          <ScrollArea h="calc(100vh - 80px)">
            <Stack gap="sm">
              {loadingLogs ? (
                <Text size="sm" c="dimmed">
                  Loading logs...
                </Text>
              ) : logs.length === 0 ? (
                <Text size="sm" c="dimmed">
                  No history logs yet.
                </Text>
              ) : (
                logs.map((log) => (
                  <Paper key={log.id} p="xs" withBorder radius="md">
                    <Stack gap={4}>
                      <Group gap="xs">
                        <Avatar size={20} radius="xl">
                          {log.user?.email.charAt(0).toUpperCase()}
                        </Avatar>
                        <Text size="sm" fw={500}>
                          {log.user?.email}
                        </Text>
                      </Group>
                      <Text size="sm">{log.action}</Text>
                      <Text size="xs" c="dimmed">
                        {dayjs(log.createdAt).format("DD.MM HH:mm")}
                      </Text>
                    </Stack>
                  </Paper>
                ))
              )}
            </Stack>
          </ScrollArea>
        </Drawer>

        <Grid gutter="xl">
          {/* Main Content Column */}
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="xl">
              {/* Title Section */}
              <Box style={{ position: "relative" }}>
                <TextInput
                  placeholder="Enter task title..."
                  value={title}
                  variant="unstyled"
                  styles={{
                    input: {
                      fontSize: "22px",
                      fontWeight: 600,
                      padding: 0,
                      minHeight: "auto",
                      lineHeight: 1.2,
                    },
                  }}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setDirtyFields((prev) => new Set(prev).add("title"));
                  }}
                  required
                  disabled={!canEdit}
                />
              </Box>

              {/* Description Section */}
              <Box>
                <Group justify="space-between" mb="xs">
                  <Group gap="xs">
                    <Text fw={600} size="md">
                      Description
                    </Text>
                    <Badge variant="light" color="blue" size="xs">
                      Markdown
                    </Badge>
                  </Group>
                  {canEdit && !isEditingDescription && (
                    <Button
                      variant="subtle"
                      size="xs"
                      leftSection={<IconEdit size={14} />}
                      onClick={() => {
                        setIsEditingDescription(true);
                      }}
                    >
                      Edit
                    </Button>
                  )}
                </Group>

                <Paper
                  withBorder
                  radius="md"
                  shadow="xs"
                  p={0}
                  style={{ overflow: "hidden" }}
                >
                  <MarkdownEditor
                    value={description}
                    onChange={(val) => {
                      setDescription(val);
                      setDirtyFields((prev) =>
                        new Set(prev).add("description"),
                      );
                    }}
                    placeholder="Add a detailed description..."
                    minRows={8}
                    disabled={!canEdit}
                    isEditing={isEditingDescription}
                    onToggleEdit={() => {
                      const newEditing = !isEditingDescription;
                      setIsEditingDescription(newEditing);
                      if (!newEditing) {
                        // No-op
                      }
                    }}
                  />
                </Paper>
              </Box>

              {/* Comments Section */}
              {task && (
                <Box>
                  <Divider
                    mb="xl"
                    label={
                      <Text fw={600} c="dimmed">
                        Activity & Comments
                      </Text>
                    }
                    labelPosition="center"
                  />

                  <Stack gap="md" mb="xl">
                    {comments.length === 0 ? (
                      <Paper
                        p="lg"
                        withBorder
                        radius="md"
                        style={{ textAlign: "center", borderStyle: "dashed" }}
                      >
                        <Text c="dimmed" size="sm">
                          No comments yet. Start the conversation!
                        </Text>
                      </Paper>
                    ) : (
                      comments.map((comment) => (
                        <Group
                          key={comment.id}
                          align="flex-start"
                          wrap="nowrap"
                          gap="sm"
                        >
                          <Avatar radius="xl" color="blue" size="md">
                            {comment.user?.email.charAt(0).toUpperCase()}
                          </Avatar>
                          <Paper
                            p="sm"
                            withBorder
                            radius="lg"
                            shadow="xs"
                            style={{
                              flex: 1,
                              backgroundColor:
                                comment.userId === currentUser.id
                                  ? "var(--mantine-color-blue-light)"
                                  : "white",
                            }}
                          >
                            <Group justify="space-between" mb={4}>
                              <Group gap="xs">
                                <Text size="sm" fw={700}>
                                  {comment.user?.email}
                                </Text>
                                <Text size="xs" c="dimmed">
                                  {dayjs(comment.createdAt).format(
                                    "HH:mm, DD MMM",
                                  )}
                                </Text>
                              </Group>
                              {(comment.userId === currentUser?.id ||
                                isAdmin) && (
                                <Group gap={4}>
                                  <ActionIcon
                                    size="xs"
                                    variant="subtle"
                                    onClick={() => {
                                      setEditingCommentId(comment.id);
                                      setEditingCommentContent(comment.content);
                                    }}
                                  >
                                    <IconEdit size={12} />
                                  </ActionIcon>
                                  <ActionIcon
                                    size="xs"
                                    variant="subtle"
                                    color="red"
                                    onClick={() =>
                                      handleDeleteComment(comment.id)
                                    }
                                  >
                                    <IconTrash size={12} />
                                  </ActionIcon>
                                </Group>
                              )}
                            </Group>

                            {editingCommentId === comment.id ? (
                              <Stack gap="xs">
                                <Textarea
                                  value={editingCommentContent}
                                  onChange={(e) =>
                                    setEditingCommentContent(e.target.value)
                                  }
                                  minRows={2}
                                  autosize
                                />
                                <Group justify="flex-end" gap="xs">
                                  <Button
                                    size="xs"
                                    variant="subtle"
                                    color="gray"
                                    onClick={() => setEditingCommentId(null)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="xs"
                                    color="green"
                                    onClick={() =>
                                      handleEditComment(comment.id)
                                    }
                                  >
                                    Save Changes
                                  </Button>
                                </Group>
                              </Stack>
                            ) : (
                              <Stack gap="xs">
                                {comment.content && (
                                  <Text
                                    size="sm"
                                    style={{ whiteSpace: "pre-wrap" }}
                                  >
                                    {comment.content}
                                  </Text>
                                )}
                                {comment.imageName && (
                                  <Box style={{ maxWidth: "200px" }}>
                                    <Image
                                      src={
                                        comment.imageData
                                          ? `data:${comment.imageMimeType};base64,${comment.imageData}`
                                          : `${process.env.REACT_APP_API_BASE_URL}/workflow/comments/${comment.id}/image`
                                      }
                                      alt={comment.imageName}
                                      radius="md"
                                      style={{ cursor: "zoom-in" }}
                                      onClick={() =>
                                        setPreviewImage({
                                          src: comment.imageData
                                            ? `data:${comment.imageMimeType};base64,${comment.imageData}`
                                            : `${process.env.REACT_APP_API_BASE_URL}/workflow/comments/${comment.id}/image`,
                                          title:
                                            comment.imageName ||
                                            "Comment Image",
                                        })
                                      }
                                    />
                                  </Box>
                                )}
                              </Stack>
                            )}
                          </Paper>
                        </Group>
                      ))
                    )}
                  </Stack>

                  {/* New Comment Input */}
                  <Paper p="md" withBorder radius="lg" shadow="sm">
                    <Stack gap="xs">
                      {commentImagePreview && (
                        <Box
                          style={{ position: "relative", width: "fit-content" }}
                        >
                          <Image
                            src={commentImagePreview}
                            w={100}
                            h={100}
                            fit="cover"
                            radius="md"
                          />
                          <ActionIcon
                            size="xs"
                            color="red"
                            variant="filled"
                            circle
                            style={{ position: "absolute", top: -8, right: -8 }}
                            onClick={() => {
                              setCommentImage(null);
                              setCommentImagePreview(null);
                            }}
                          >
                            <IconX size={10} />
                          </ActionIcon>
                        </Box>
                      )}
                      <Group
                        gap="xs"
                        align="flex-end"
                        className="workflow-comment-composer"
                        style={{ position: "relative" }}
                      >
                        <Avatar radius="xl" color="blue" size="md">
                          {currentUser?.email.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box style={{ flex: 1 }}>
                          <Textarea
                            placeholder="Writing a comment... (use Ctrl+Enter to send)"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            variant="unstyled"
                            autosize
                            minRows={1}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && e.ctrlKey) {
                                handleAddComment();
                              }
                            }}
                            styles={{
                              input: { padding: "8px 0" },
                            }}
                          />
                        </Box>
                        <Group gap={6}>
                          <FileButton
                            onChange={(file) => {
                              setCommentImage(file);
                              handleBlur("newComment");
                            }}
                            accept="image/*"
                          >
                            {(props) => (
                              <ActionIcon
                                {...props}
                                size="lg"
                                variant="subtle"
                                color="gray"
                                onClick={(e) => {
                                  props.onClick(e);
                                }}
                              >
                                <IconPhoto size={20} />
                              </ActionIcon>
                            )}
                          </FileButton>
                          <ActionIcon
                            size="lg"
                            radius="xl"
                            variant="filled"
                            color="blue"
                            onClick={handleAddComment}
                            disabled={!newComment.trim() && !commentImage}
                          >
                            <IconSend size={20} />
                          </ActionIcon>
                        </Group>
                      </Group>
                    </Stack>
                  </Paper>
                </Box>
              )}
            </Stack>
          </Grid.Col>

          {/* Sidebar Column */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap="lg">
              <Paper withBorder p="md" radius="md">
                <Stack gap="md">
                  <Text fw={700} size="sm" c="dimmed" tt="uppercase" lts="1px">
                    Details
                  </Text>

                  {task && task.creator && (
                    <Box>
                      <Text size="xs" fw={700} c="dimmed" mb={4}>
                        Author
                      </Text>
                      <Group gap="xs">
                        <Avatar size={18} radius="xl" color="gray">
                          {task.creator.email.charAt(0).toUpperCase()}
                        </Avatar>
                        <Text size="xs">{task.creator.email}</Text>
                      </Group>
                    </Box>
                  )}

                  <Box style={{ position: "relative" }}>
                    <Select
                      label="Assignee"
                      placeholder="Unassigned"
                      data={userOptions}
                      value={assignedToId}
                      leftSection={<Avatar size={18} radius="xl" />}
                      onChange={(val) => {
                        setAssignedToId(val);
                        setDirtyFields((prev) =>
                          new Set(prev).add("assignedToId"),
                        );
                      }}
                      clearable
                      disabled={!canEdit}
                    />
                  </Box>

                  <Box style={{ position: "relative" }}>
                    <Select
                      label="Priority"
                      data={priorityOptions}
                      value={priority}
                      onChange={(val) => {
                        setPriority(val);
                        setDirtyFields((prev) => new Set(prev).add("priority"));
                      }}
                      disabled={!canEdit}
                    />
                  </Box>

                  <Box style={{ position: "relative" }}>
                    <DatePickerInput
                      label="Due Date"
                      placeholder="No deadline"
                      value={dueDate}
                      onChange={(val) => {
                        setDueDate(val);
                        setDirtyFields((prev) => new Set(prev).add("dueDate"));
                      }}
                      popoverProps={{ withinPortal: true }}
                      clearable
                      disabled={!canEdit}
                    />
                  </Box>

                  <Box style={{ position: "relative" }}>
                    <TagsInput
                      label="Tags"
                      placeholder="Add tags..."
                      value={tags}
                      onChange={(val) => {
                        setTags(val);
                        setDirtyFields((prev) => new Set(prev).add("tags"));
                      }}
                      disabled={!canEdit}
                    />
                  </Box>
                </Stack>
              </Paper>

              <Paper withBorder p="md" radius="md">
                <Stack gap="md">
                  <Text fw={700} size="sm" c="dimmed" tt="uppercase" lts="1px">
                    Participants & Queue
                  </Text>
                  <Stack gap="xs">
                    {watchers.map((email, index) => (
                      <Group
                        key={`${email}-${index}`}
                        justify="space-between"
                        wrap="nowrap"
                      >
                        <Group gap="xs">
                          <Avatar
                            size="sm"
                            radius="xl"
                            color={email === activeEditor ? "blue" : "gray"}
                          >
                            {email.charAt(0).toUpperCase()}
                          </Avatar>
                          <Text
                            size="xs"
                            fw={email === activeEditor ? 600 : 400}
                          >
                            {email.split("@")[0]}
                          </Text>
                        </Group>
                        {index === 0 ? (
                          <Badge size="xs" color="blue" variant="filled">
                            Editor
                          </Badge>
                        ) : (
                          <Badge size="xs" color="gray" variant="outline">
                            #{index + 1}
                          </Badge>
                        )}
                      </Group>
                    ))}
                    {watchers.length === 0 && (
                      <Text size="xs" c="dimmed" italic>
                        No active watchers
                      </Text>
                    )}
                  </Stack>
                  {isEditor && watchers.length > 1 && (
                    <Text size="10px" c="blue" italic>
                      You are the active editor. Others are in queue.
                    </Text>
                  )}
                </Stack>
              </Paper>

              {task && (
                <Paper withBorder p="md" radius="md">
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Text
                        fw={700}
                        size="sm"
                        c="dimmed"
                        tt="uppercase"
                        lts="1px"
                      >
                        Attachments
                      </Text>
                      <Box style={{ position: "relative" }}>
                        <FileButton onChange={handleFileUpload} accept="*">
                          {(props) => (
                            <ActionIcon
                              {...props}
                              variant="light"
                              color="blue"
                              onClick={(e) => {
                                props.onClick(e);
                              }}
                            >
                              <IconPaperclip size={16} />
                            </ActionIcon>
                          )}
                        </FileButton>
                      </Box>
                    </Group>

                    {attachments.length === 0 ? (
                      <Text size="xs" c="dimmed">
                        No files attached
                      </Text>
                    ) : (
                      <Stack gap="xs">
                        {attachments.map((att) => (
                          <Card
                            key={att.id}
                            p="xs"
                            withBorder
                            shadow="none"
                            radius="md"
                          >
                            <Group justify="space-between" wrap="nowrap">
                              <Group
                                gap="xs"
                                style={{ flex: 1, overflow: "hidden" }}
                              >
                                {att.mimetype?.startsWith("image/") ? (
                                  <Image
                                    src={
                                      att.base64Data
                                        ? `data:${att.mimetype};base64,${att.base64Data}`
                                        : `${process.env.REACT_APP_API_BASE_URL}/workflow/attachments/${att.id}/download`
                                    }
                                    w={32}
                                    h={32}
                                    radius="xs"
                                    fit="cover"
                                    style={{ cursor: "zoom-in" }}
                                    onClick={() =>
                                      setPreviewImage({
                                        src: att.base64Data
                                          ? `data:${att.mimetype};base64,${att.base64Data}`
                                          : `${process.env.REACT_APP_API_BASE_URL}/workflow/attachments/${att.id}/download`,
                                        title:
                                          att.filename || "Attachment Image",
                                      })
                                    }
                                  />
                                ) : (
                                  <ThemeIcon
                                    variant="light"
                                    color="gray"
                                    size="md"
                                  >
                                    <IconPaperclip size={14} />
                                  </ThemeIcon>
                                )}
                                <Box style={{ flex: 1, overflow: "hidden" }}>
                                  <Text size="xs" fw={500} truncate>
                                    {att.filename}
                                  </Text>
                                  <Text size="10px" c="dimmed">
                                    {(att.filesize / 1024).toFixed(1)} KB
                                  </Text>
                                </Box>
                              </Group>
                              <Group gap={2}>
                                <ActionIcon
                                  variant="subtle"
                                  size="sm"
                                  onClick={() =>
                                    window.open(
                                      att.base64Data
                                        ? `data:${att.mimetype};base64,${att.base64Data}`
                                        : `${process.env.REACT_APP_API_BASE_URL}/workflow/attachments/${att.id}/download`,
                                      "_blank",
                                    )
                                  }
                                >
                                  <IconDownload size={14} />
                                </ActionIcon>
                                {(att.userId === currentUser?.id ||
                                  isAdmin) && (
                                  <ActionIcon
                                    variant="subtle"
                                    size="sm"
                                    color="red"
                                    onClick={() =>
                                      handleDeleteAttachment(att.id)
                                    }
                                  >
                                    <IconTrash size={14} />
                                  </ActionIcon>
                                )}
                              </Group>
                            </Group>
                          </Card>
                        ))}
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Grid.Col>
        </Grid>

        <Divider mt="xl" mb="md" />
        <Group justify="flex-end" gap="sm">
          {task && (
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={handleDelete}
              disabled={
                !canEdit ||
                Object.keys(lockedFields).some(
                  (k) => lockedFields[k] !== currentUser?.email,
                )
              }
              mr="auto"
            >
              Delete Task
            </Button>
          )}
          <Button variant="light" color="gray" onClick={handleModalClose}>
            {dirtyFields.size > 0 && isEditor ? "Discard Changes" : "Close"}
          </Button>
          {(task ? isEditor : true) && (
            <Button
              onClick={handleSave}
              loading={loading}
              radius="md"
              size="md"
              px="xl"
              disabled={
                title.trim().length === 0 || (task && dirtyFields.size === 0)
              }
            >
              {task ? "Save Changes" : "Create Task"}
            </Button>
          )}
        </Group>
      </Modal>

      {/* In-app Image Preview Modal */}
      <Modal
        opened={!!previewImage}
        onClose={() => setPreviewImage(null)}
        title={previewImage?.title || "Image Preview"}
        size="auto"
        centered
        overlayProps={{
          backgroundOpacity: 0.55,
          blur: 3,
        }}
      >
        {previewImage && (
          <Image
            src={previewImage.src}
            fit="contain"
            style={{ maxWidth: "90vw", maxHeight: "80vh" }}
          />
        )}
      </Modal>
    </>
  );
};

export default WorkflowTaskModal;
